import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The company's logo wherever it shows (the top bar, the menu, sign-in). A wide logo (say 300×100) stands on its own
 * in place of the name; a square one sits in the tile; with no logo, the initials. Dark artwork gets a light backing
 * so it never disappears on the black bar.
 */
export function CompanyLogo({ branding, name, className, on }) {
    const initials = name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    if (!branding.logoUrl)
        return _jsx("span", { className: `tc-clogo initials${className ? ` ${className}` : ""}`, "aria-hidden": "true", children: initials });
    // On a light background, the company's light-background version if it has one (dark words, no backing needed).
    if (on === "light" && branding.logoLightUrl) {
        const shape = branding.logoLightShape ?? branding.logoShape ?? "square";
        return (_jsx("span", { className: `tc-clogo ${shape} tone-dark for-light${className ? ` ${className}` : ""}`, children: _jsx("img", { src: branding.logoLightUrl, alt: shape === "wide" ? name : "" }) }));
    }
    const shape = branding.logoShape ?? "square";
    return (_jsx("span", { className: `tc-clogo ${shape} tone-${branding.logoTone ?? "dark"}${className ? ` ${className}` : ""}`, children: _jsx("img", { src: branding.logoUrl, alt: shape === "wide" ? name : "" }) }));
}
/**
 * Turn an uploaded file (PNG, JPEG, WebP or SVG) into a small picture for the logo: wide logos fit 600×200, square
 * ones 256×256. Also works out its shape and whether the artwork is dark or light.
 */
export async function prepareLogo(file) {
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type))
        throw new Error("Choose a PNG, JPG, WebP or SVG file.");
    if (file.size > 5 * 1024 * 1024)
        throw new Error("That file is over 5 MB. Choose a smaller one.");
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((ok, fail) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => fail(new Error("That image couldn't be read.")); i.src = url; });
        // Trim see-through margins first, so the artwork fills the space it's given.
        const nw = img.naturalWidth || 600, nh = img.naturalHeight || 200;
        const s0 = Math.min(1, 1600 / Math.max(nw, nh));
        const t = document.createElement("canvas");
        t.width = Math.round(nw * s0);
        t.height = Math.round(nh * s0);
        const tg = t.getContext("2d");
        tg.drawImage(img, 0, 0, t.width, t.height);
        const a = tg.getImageData(0, 0, t.width, t.height).data;
        let x0 = t.width, y0 = t.height, x1 = -1, y1 = -1;
        for (let y = 0; y < t.height; y++)
            for (let x = 0; x < t.width; x++)
                if (a[(y * t.width + x) * 4 + 3] > 12) {
                    if (x < x0)
                        x0 = x;
                    if (x > x1)
                        x1 = x;
                    if (y < y0)
                        y0 = y;
                    if (y > y1)
                        y1 = y;
                }
        const box = x1 < 0 ? { x: 0, y: 0, w: t.width, h: t.height } : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
        const w0 = box.w, h0 = box.h;
        const shape = w0 / h0 >= 1.6 ? "wide" : "square";
        const [mw, mh] = shape === "wide" ? [600, 200] : [256, 256];
        const k = Math.min(mw / w0, mh / h0, file.type === "image/svg+xml" ? 10 : 1 / s0);
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(w0 * k));
        c.height = Math.max(1, Math.round(h0 * k));
        const g = c.getContext("2d");
        g.drawImage(t, box.x, box.y, box.w, box.h, 0, 0, c.width, c.height);
        // Dark or light artwork: the average brightness of the pixels that aren't see-through.
        const px = g.getImageData(0, 0, c.width, c.height).data;
        let sum = 0, n = 0;
        for (let i = 0; i < px.length; i += 16)
            if (px[i + 3] > 40) {
                sum += 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
                n++;
            }
        const opaque = n / (px.length / 16);
        // A logo on its own solid background (no see-through) is shown as it is, on a light backing.
        const tone = opaque > 0.92 ? "dark" : n && sum / n > 150 ? "light" : "dark";
        let dataUrl = c.toDataURL("image/png");
        if (dataUrl.length > 280_000)
            dataUrl = c.toDataURL("image/webp", 0.9);
        if (dataUrl.length > 280_000)
            throw new Error("That logo is too detailed to use. Try a simpler or smaller file.");
        return { dataUrl, shape, tone };
    }
    finally {
        URL.revokeObjectURL(url);
    }
}
